import { useAuth0 } from "@auth0/auth0-react";
import { datadogRum } from "@datadog/browser-rum";
import { ApiStatusToFileStatus } from "@utils/constants";
import { convertHeadersToObject, graphqlErrorHandler } from "@utils/utils";
import { GraphQLError } from "graphql";
import { useCallback } from "react";
import { useParams } from "react-router-dom";

import { FileUploadAndParseProps } from "@fronterahealth/frontera-ui-components";

import { FronteraGraphQLClient } from "@api/graphql/graphql-service";
import {
  ApiLearnerFileStatusChoices,
  CreateReportLearnerFileInputMutation,
  FileTypeEnum,
  GetReportLearnerFilesDocument,
  LearnerFileAssessmentTemplateTypeEnums,
  MarkLearnerFileAsUploadedMutation,
  StartLearnerFileClassificationMutation,
  StartLearnerFileValidationMutation,
  StartReassessmentFileClassificationMutation,
  useCreateReportLearnerFileInputMutation,
  useDeleteLearnerFileMutation,
  useMarkLearnerFileAsUploadedMutation,
  useStartLearnerFileClassificationMutation,
  useStartLearnerFileValidationMutation,
  useStartReassessmentFileClassificationMutation,
} from "@api/graphql/types-and-hooks";
import { isTranscribeMime } from "@components/UploadDocuments/transcribeLanguage";
import { notifyError, notifySuccess } from "@components/notifications/notifications";
import { useReport } from "@providers/ReportProvider";

type ExtendedFileUploadAndParseProps = {
  S3BucketUrlRetriever: (params: {
    fileKind: string;
    fileName: string;
    mimeType: string;
    fileSize: number;
    assessmentTemplateType?: LearnerFileAssessmentTemplateTypeEnums;
  }) => Promise<{ bucket: string; fileId: string }>;
};

export const useGetS3BucketUrlRetriever: ({
  metadata,
  assessmentTemplateType,
  transcribeLanguage,
}: {
  metadata?: object;
  assessmentTemplateType?: LearnerFileAssessmentTemplateTypeEnums;
  // When set, audio/video files are pre-classified as INTERVIEW with
  // metadata: { language }. interview_type is intentionally omitted so the backend
  // infers the respondent from the filename (defaulting to "other").
  transcribeLanguage?: string;
}) => ExtendedFileUploadAndParseProps["S3BucketUrlRetriever"] = ({ metadata: _metadata, transcribeLanguage }) => {
  const { reportId } = useParams();
  const createReportLearnerFileInputMutation = useCreateReportLearnerFileInputMutation({
    throwOnError: false,
  });
  const baseMetadataString = _metadata ? JSON.stringify(_metadata) : undefined;
  return async ({ fileKind, fileName, mimeType, fileSize, assessmentTemplateType }) => {
    let effectiveFileKind = fileKind;
    let effectiveMetadata = baseMetadataString;

    if (transcribeLanguage && isTranscribeMime(mimeType)) {
      effectiveFileKind = FileTypeEnum.Interview;
      // Omit interview_type so the backend infers the respondent from the filename.
      // Hardcoding "other" here previously pre-empted that inference.
      effectiveMetadata = JSON.stringify({
        language: transcribeLanguage,
      });
    }

    console.debug(
      `%c<FILE-API> | S3BucketURLRetriever | Requesting S3 Bucket URL and creating file record |` +
        `%c fileKind: ${effectiveFileKind} fileName: ${fileName} mimeType: ${mimeType} assessmentId: ${reportId} metadata: ${effectiveMetadata}`,
      `color:black`,
      `color:red`,
    );

    let res: CreateReportLearnerFileInputMutation | null;
    try {
      res = await createReportLearnerFileInputMutation.mutateAsync({
        input: {
          fileKind: effectiveFileKind as FileTypeEnum,
          fileSize,
          mimeType,
          originalFileName: fileName,
          reportId: reportId || "<missing-report-id>",
          metadata: effectiveMetadata,
          assessmentTemplateType: assessmentTemplateType,
        },
      });

      datadogRum.addAction("create-learner-file", {
        fileMetadata: {
          fileKind: effectiveFileKind,
          mimeType,
          fileName,
          metadata: effectiveMetadata,
          fileId: res?.createReportLearnerFile?.learnerFile?.id,
        },
      });

      console.debug(
        `<FILE-API> | S3BucketURLRetriever | Received following bucket information | ` + `%c${JSON.stringify(res)}`,
        "color:red;",
      );
      const learnerFile = res?.createReportLearnerFile?.learnerFile;
      const bucket = learnerFile?.signedS3Url || "<missing-bucket-id>";
      const fileId = learnerFile?.id || "<missing-file-id>";
      //
      return {
        bucket,
        fileId,
      };
    } catch (err) {
      console.error(
        `<FILE-API> | S3BucketURLRetriever | ERROR Received following bucket information | ` +
          `%c${JSON.stringify(err)}`,
        "color:red;",
      );

      try {
        const errorMessages = graphqlErrorHandler(err as GraphQLError);
        for (const errorMessage of errorMessages) {
          notifyError(errorMessage);
        }
      } catch (parseError) {
        console.error("Failed to parse JSON from error message:", parseError);
      }

      throw new Error(String(err));
    }
  };
};

export type FileUploadMode = "validate" | "classify" | "reassessment-classify";

export const useGetReportFileUploadCompleteCallback = ({
  classify = false,
  mode,
}: { classify?: boolean; mode?: FileUploadMode } = {}): FileUploadAndParseProps["reportFileUploadCompleteCallback"] => {
  const markLearnerFileAsUploadedMutation = useMarkLearnerFileAsUploadedMutation({
    throwOnError: false,
  });

  // Call all hooks unconditionally to satisfy React Rules of Hooks
  const startLearnerFileValidationMutation = useStartLearnerFileValidationMutation({
    throwOnError: false,
  });

  const startLearnerFileClassificationMutation = useStartLearnerFileClassificationMutation({
    throwOnError: false,
  });

  const startReassessmentFileClassificationMutation = useStartReassessmentFileClassificationMutation({
    throwOnError: false,
  });

  // Determine effective mode: explicit mode takes precedence, then classify flag for backward compat
  const effectiveMode: FileUploadMode = mode ?? (classify ? "classify" : "validate");

  // Select which mutation to use based on mode
  const startLearnerFileWorkflowMutation =
    effectiveMode === "reassessment-classify"
      ? startReassessmentFileClassificationMutation
      : effectiveMode === "classify"
        ? startLearnerFileClassificationMutation
        : startLearnerFileValidationMutation;

  const { reportQuery } = useReport();
  const { refetch } = reportQuery;

  return async (fileId) => {
    console.debug(
      `<FILE-API> | reportFileUploadCompleteCallback | Reporting file upload complete to FDM | ` +
        `%cfileId: ${fileId}`,
      "color:red;",
    );
    let res: MarkLearnerFileAsUploadedMutation | null;
    let fileWorkflowRes:
      | StartLearnerFileValidationMutation
      | StartLearnerFileClassificationMutation
      | StartReassessmentFileClassificationMutation
      | null;
    try {
      res = await markLearnerFileAsUploadedMutation.mutateAsync({
        fileId,
      });

      datadogRum.addAction("mark-learner-file-as-uploaded", {
        fileMetadata: {
          fileId,
        },
      });

      console.debug(`<FILE-API> | reportFileUploadCompleteCallback | Done reporting file upload complete to FDM`);
      console.debug(``);
      const status = res?.markLearnerFileAsUploaded?.learnerFile?.status;
      console.debug({ status });

      const workflowName =
        effectiveMode === "reassessment-classify"
          ? "startReassessmentFileClassification"
          : effectiveMode === "classify"
            ? "startLearnerFileClassification"
            : "startLearnerFileValidation";
      const workflowAction = effectiveMode === "validate" ? "file parsing" : "file classification";
      console.debug(
        `<FILE-API> | ${workflowName} | Reporting to start ${workflowAction} to FDM | ` + `%cfileId: ${fileId}`,
        "color:red;",
      );

      fileWorkflowRes = await startLearnerFileWorkflowMutation.mutateAsync({
        learnerFileId: fileId,
      });

      await refetch();

      console.debug(`<FILE-API> | ${workflowName} | Done reporting ${workflowAction} to FDM`);
      if (effectiveMode === "reassessment-classify" && "startReassessmentFileClassification" in fileWorkflowRes) {
        console.debug(
          fileWorkflowRes.startReassessmentFileClassification?.status,
          fileWorkflowRes.startReassessmentFileClassification?.learnerFile?.status,
        );
      } else if (effectiveMode === "classify" && "startLearnerFileClassification" in fileWorkflowRes) {
        console.debug(
          fileWorkflowRes.startLearnerFileClassification?.status,
          fileWorkflowRes.startLearnerFileClassification?.learnerFile?.status,
        );
      } else if ("startLearnerFileValidation" in fileWorkflowRes) {
        console.debug(
          fileWorkflowRes.startLearnerFileValidation?.status,
          fileWorkflowRes.startLearnerFileValidation?.learnerFile?.status,
        );
      }
      return;
    } catch (err) {
      console.error(
        `<FILE-API> | reportFileUploadCompleteCallback | ERROR Received following bucket information | ` +
          `%c${JSON.stringify(err)}`,
        "color:red;",
      );
      throw new Error(String(err));
    }
  };
};

export const useGetS3BucketUploadCallback: () => FileUploadAndParseProps["S3BucketUploadCallback"] = () => {
  const { reportQuery } = useReport();
  const { refetch } = reportQuery;

  return async (params) => {
    const { file, mimeType, url } = params;
    console.debug(
      `<FILE-API> | S3BucketUploadCallback | Sending file contents directly to S3 through S3 SDK | ` +
        `%c file: ${file} url: ${url} mimeType: ${mimeType}`,
      "color:red;",
    );
    try {
      await refetch();
      const response = await fetch(url, {
        method: "PUT",
        headers: new Headers({ "Content-Type": mimeType }),
        body: file,
      });

      console.debug(`<FILE-API> | S3BucketUploadCallback | Done sending data to S3`);
      console.debug(``);
      if (response.status === 200) {
        console.debug({ response });
        datadogRum.addAction("learner-file-uploaded-successfully", {
          fileMetadata: {
            fileName: file.name,
            mimeType,
          },
        });
        return;
      } else {
        console.error(`<FILE-API> | S3BucketUploadCallback | Error in sending data`);
        throw new Error(`Non 200 error message: ${response.status} ${response.statusText}`);
      }
    } catch (err) {
      console.error("Error calling S3BucketUploadCallback", err);
      throw new Error(String(err));
    }
  };
};

export const useGetFileParseStatusRetriever: () => FileUploadAndParseProps["fileParseStatusRetriever"] = () => {
  const { isAuthenticated, getAccessTokenSilently } = useAuth0();

  const { reportId } = useParams();
  // const headers = convertHeadersToObject(authHeaders);

  const { reportQuery } = useReport();
  const { refetch } = reportQuery;

  return useCallback(
    async (fileId: string) => {
      try {
        const token = await getAccessTokenSilently();
        const authHeaders: HeadersInit = new Headers();
        authHeaders.append("Authorization", `Bearer ${token}`);
        const headers = convertHeadersToObject(authHeaders);
        const response = await FronteraGraphQLClient.request(
          GetReportLearnerFilesDocument,
          { reportId, fileId },
          headers,
        );

        const learnerFileStatusContext = response?.getReportLearnerFiles?.edges?.[0]?.node;

        const apiStatus: ApiLearnerFileStatusChoices =
          learnerFileStatusContext?.status ||
          ("<missing-status" as ApiLearnerFileStatusChoices as ApiLearnerFileStatusChoices);
        const status = ApiStatusToFileStatus[apiStatus];
        const fileKind = learnerFileStatusContext?.fileKind;

        // Upload steps
        if (
          status === "uploading-failed-bucket-upload" ||
          status === "uploading-failed-upload-notification" ||
          status === "uploading-failed-url-retrieval"
        ) {
          datadogRum.addError("file-upload-failed", {
            fileMetadata: {
              fileId,
              status,
            },
          });
          datadogRum.addAction("file-upload-failed", {
            fileMetadata: {
              fileId,
              status,
            },
          });
          await refetch();
        }

        // Classification steps
        if (status === "classifying-success") {
          datadogRum.addAction("file-classification-succeeded", {
            fileMetadata: {
              fileId,
              status,
            },
          });
          // no refetch here as the classification success will trigger parsing automatically
        }
        if (status === "classifying-error") {
          datadogRum.addError("file-classification-failed", {
            fileMetadata: {
              fileId,
              status,
            },
          });
          datadogRum.addAction("file-classification-failed", {
            fileMetadata: {
              fileId,
              status,
            },
          });
          await refetch();
        }

        // Parsing steps
        if (status === "parsing-failed") {
          datadogRum.addError("file-parsing-failed", {
            fileMetadata: {
              fileId,
              status,
            },
          });
          datadogRum.addAction("file-parsing-failed", {
            fileMetadata: {
              fileId,
              status,
            },
          });
          await refetch();
        }

        // Done
        if (status === "done") {
          datadogRum.addAction("file-parsing-succeeded", {
            fileMetadata: {
              fileId,
              status,
            },
          });
          await refetch();
        }
        console.debug(`<FILE-API> | fileParseStatusRetriever | Got the following parse status from FDM | ${status}`);
        console.debug(``);

        return {
          fileId,
          status,
          failureReason: learnerFileStatusContext?.processingFailureReason,
          failureCode: learnerFileStatusContext?.processingFailureCode,
          fileKind,
        };
      } catch (err) {
        console.error(
          `<FILE-API> | fileParseStatusRetriever | ERROR Received error trying to get parse status | ` +
            `%c${JSON.stringify(err)}`,
          "color:red;",
        );
        throw new Error(String(err));
      }
    },
    [reportId, isAuthenticated],
  );
};

export const useGetRemoveFileCallback: () => FileUploadAndParseProps["removeFileCallback"] = () => {
  const deleteLearnerFileMutation = useDeleteLearnerFileMutation({
    throwOnError: false,
  });

  const { reportQuery } = useReport();

  const { refetch } = reportQuery;
  return async (fileId: string) => {
    try {
      await deleteLearnerFileMutation.mutateAsync({ fileId });
      await refetch();
      notifySuccess(`Successfully deleted file`);
    } catch (err) {
      console.error("Error with deleting file", err);
      notifyError(`Failed to delete file. Something went wrong on our end`);
    }
  };
};
